/** Read the radar time slices published by the Weather Company through ro-tiles. */

import {
  buildWeathercomInventoryUrl,
  type RadarTimestamp,
} from '@/utils/radarProvider';

const FRAME_COUNT = 12;
const INTERVAL_SECONDS = 5 * 60;

interface RadarInventory {
  seriesInfo?: {
    twcRadarMosaic?: {
      series?: Array<{ ts?: unknown }>;
    };
  };
}

/** Keep only real, recent frame times rather than inventing missing slots. */
export async function fetchWeathercomRadarTimestamps(): Promise<RadarTimestamp[]> {
  const response = await fetch(buildWeathercomInventoryUrl());
  if (!response.ok) {
    throw new Error(`Weather.com radar inventory unavailable (${response.status})`);
  }

  const inventory = await response.json() as RadarInventory;
  const series = inventory?.seriesInfo?.twcRadarMosaic?.series;
  if (!Array.isArray(series)) {
    throw new Error('Weather.com radar inventory has no radar series');
  }

  const now = Math.floor(Date.now() / 1000);
  const oldestAllowed = now - FRAME_COUNT * INTERVAL_SECONDS;
  const epochs = [...new Set(series
    .map((frame) => frame.ts)
    .filter((ts): ts is number =>
      typeof ts === 'number' &&
      Number.isSafeInteger(ts) &&
      ts <= now &&
      ts >= oldestAllowed,
    ))]
    .sort((a, b) => a - b)
    .slice(-FRAME_COUNT);

  if (epochs.length === 0) {
    throw new Error('Weather.com has no recent published radar frames');
  }

  return epochs.map((epoch) => ({ source: 'weathercom', epoch }));
}
